import React, { useEffect, useState, useMemo } from 'react';
import {
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    TimePicker,
    Radio,
    Checkbox,
    Upload,
    Button,
    Typography,
    Divider,
    Space,
    Card,
    Spin
} from 'antd';
import { UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { FormField_V2, FieldType_V2, FormSchema_V2, SqliteFieldConfig } from '../../../common/typescript/models/CollectFormV2';
import initSqlJs from 'sql.js';

// Minimal type definition or use any since @types/sql.js is missing
type SqlJsStatic = any;

const { TextArea } = Input;
const { Title, Text } = Typography;

interface FormPreviewV2Props {
    schema: FormSchema_V2;
    sqliteFiles?: File[];
}

// Extract variable names from ${variable} pattern
const extractDependencies = (whereClause?: string): string[] => {
    if (!whereClause) return [];
    const regex = /\$\{([^}]+)\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(whereClause)) !== null) {
        matches.push(match[1]);
    }
    return matches;
};

const SqliteSelect: React.FC<{
    field: FormField_V2;
    sqliteFiles: File[];
    sqlJs: SqlJsStatic | null;
}> = ({ field, sqliteFiles, sqlJs }) => {
    const config = field.config as SqliteFieldConfig;
    const form = Form.useFormInstance();
    const [options, setOptions] = useState<{ label: string; value: any }[]>([]);
    const [loading, setLoading] = useState(false);

    // Identify dependencies from WHERE clause
    const dependencies = useMemo(() => extractDependencies(config.where), [config.where]);

    // Watch all values to handle dynamic dependencies
    const allValues = Form.useWatch([], form);

    useEffect(() => {
        const fetchOptions = async () => {
            console.log("Fetching options for", field.label, { config, sqlJs: !!sqlJs });

            if (!config.database || !config.table || !config.valueColumn || !config.labelColumn || !sqlJs) {
                return;
            }

            // Check if all dependencies have values
            const currentValues = form.getFieldsValue();
            const missingDependency = dependencies.find(dep => !currentValues[dep]);

            if (missingDependency) {
                console.log("Missing dependency:", missingDependency);
                setOptions([]);
                return;
            }

            const file = sqliteFiles.find(f => f.name === config.database);
            if (!file) {
                console.warn(`Database file ${config.database} not found in`, sqliteFiles.map(f => f.name));
                return;
            }

            setLoading(true);
            try {
                const arrayBuffer = await file.arrayBuffer();
                const uint8Array = new Uint8Array(arrayBuffer);
                const db = new sqlJs.Database(uint8Array);

                let query = `SELECT ${config.valueColumn}, ${config.labelColumn} FROM ${config.table}`;

                // Process WHERE clause
                if (config.where) {
                    let whereClause = config.where;
                    dependencies.forEach(dep => {
                        const val = currentValues[dep];
                        // Basic handling for string vs number
                        // Safe to assume string for now, but should handle properly
                        const formattedVal = typeof val === 'string' ? `'${val}'` : val;
                        whereClause = whereClause.replace(`\${${dep}}`, String(formattedVal));
                    });
                    query += ` WHERE ${whereClause}`;
                }

                if (config.orderBy) {
                    query += ` ORDER BY ${config.orderBy}`;
                }

                console.log("Executing query:", query);
                const result = db.exec(query);
                if (result.length > 0 && result[0].values.length > 0) {
                    const newOptions = result[0].values.map((row: any[]) => ({
                        value: row[0], // valueColumn
                        label: String(row[1]) // labelColumn
                    }));
                    console.log("Query result:", newOptions);
                    setOptions(newOptions);
                } else {
                    console.log("No results found");
                    setOptions([]);
                }
                db.close();
            } catch (err) {
                console.error("Error querying SQLite:", err);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchOptions();
    }, [config, sqliteFiles, sqlJs, allValues]); // Depend on allValues to re-run when form changes
    return (
        <Select
            placeholder={field.placeholder}
            style={{ width: '100%' }}
            loading={loading}
            allowClear
            disabled={loading || (dependencies.length > 0 && options.length === 0)}
        >
            {options.map((opt) => (
                <Select.Option key={String(opt.value)} value={opt.value}>
                    {opt.label}
                </Select.Option>
            ))}
        </Select>
    );
};

const FieldRenderer: React.FC<{
    field: FormField_V2;
    sqliteFiles?: File[];
    sqlJs: SqlJsStatic | null;
}> = ({ field, sqliteFiles, sqlJs }) => {
    const renderInput = () => {
        switch (field.type) {
            case FieldType_V2.TEXT:
                return <Input placeholder={field.placeholder} />;
            case FieldType_V2.TEXTAREA:
                return <TextArea rows={4} placeholder={field.placeholder} />;
            case FieldType_V2.NUMBER:
                return (
                    <InputNumber
                        style={{ width: '100%' }}
                        placeholder={field.placeholder}
                        addonAfter={(field.config as any)?.suffix}
                    />
                );
            case FieldType_V2.DATE:
                return <DatePicker style={{ width: '100%' }} placeholder={field.placeholder} />;
            case FieldType_V2.TIME:
                return <TimePicker style={{ width: '100%' }} placeholder={field.placeholder} />;
            case FieldType_V2.DATETIME:
                return <DatePicker showTime style={{ width: '100%' }} placeholder={field.placeholder} />;
            case FieldType_V2.SELECT:
            case FieldType_V2.MULTISELECT:
            case FieldType_V2.CASCADE_SELECT:
                // Check if it's a SQLite field
                const config = field.config as any;
                if (config?.sqlite && config?.database && sqliteFiles) {
                    return (
                        <SqliteSelect
                            field={field}
                            sqliteFiles={sqliteFiles}
                            sqlJs={sqlJs}
                        />
                    );
                }

                // Standard static options
                const options = config?.options || [];
                return (
                    <Select
                        mode={field.type === FieldType_V2.MULTISELECT ? 'multiple' : undefined}
                        placeholder={field.placeholder}
                        style={{ width: '100%' }}
                    >
                        {options.map((opt: any) => (
                            <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Select.Option>
                        ))}
                    </Select>
                );
            case FieldType_V2.RADIO:
                const radioOptions = (field.config as any)?.options || [];
                return (
                    <Radio.Group>
                        <Space direction="vertical">
                            {radioOptions.map((opt: any) => (
                                <Radio key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Radio>
                            ))}
                        </Space>
                    </Radio.Group>
                );
            case FieldType_V2.CHECKBOX:
                const checkOptions = (field.config as any)?.options;
                if (checkOptions && checkOptions.length > 0) {
                    return (
                        <Checkbox.Group>
                            <Space direction="vertical">
                                {checkOptions.map((opt: any) => (
                                    <Checkbox key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </Checkbox>
                                ))}
                            </Space>
                        </Checkbox.Group>
                    );
                }
                return <Checkbox>{field.label}</Checkbox>;

            case FieldType_V2.FILE:
                return (
                    <Upload>
                        <Button icon={<UploadOutlined />}>Tải tệp lên</Button>
                    </Upload>
                );

            case FieldType_V2.HEADER:
                return <Typography.Title level={5}>{field.label}</Typography.Title>;

            case FieldType_V2.DIVIDER:
                return <Divider />;

            case FieldType_V2.COMPUTED:
                return (
                    <Input
                        disabled
                        placeholder="Giá trị tính toán tự động"
                        addonAfter={(field.config as any)?.suffix}
                    />
                );

            case FieldType_V2.HTML:
                return <div dangerouslySetInnerHTML={{ __html: field.defaultValue || '' }} />;
            default:
                return <Input disabled placeholder={`Unsupported type: ${field.type}`} />;
        }
    };

    // Special handling for non-input fields to avoid Form.Item wrapper duplication if needed
    if (field.type === FieldType_V2.HEADER || field.type === FieldType_V2.DIVIDER || field.type === FieldType_V2.HTML) {
        return <div style={{ marginBottom: 16 }}>{renderInput()}</div>;
    }

    const isRequired = typeof field.required === 'boolean' ? field.required : false;

    return (
        <Form.Item
            label={field.label}
            name={field.id} // Added name prop for Form.useWatch to work correctly
            required={isRequired}
            help={field.helpText}
            layout="vertical"
        >
            {renderInput()}
        </Form.Item>
    );
};

export const FormPreviewV2: React.FC<FormPreviewV2Props> = ({ schema, sqliteFiles }) => {
    const [sqlJs, setSqlJs] = useState<SqlJsStatic | null>(null);

    useEffect(() => {
        // Initialize sql.js once
        const init = async () => {
            try {
                const SQL = await initSqlJs({
                    locateFile: (file: string) => `https://sql.js.org/dist/${file}`
                });
                setSqlJs(SQL);
            } catch (error) {
                console.error("Failed to initialize sql.js", error);
            }
        };
        init();
    }, []);

    return (
        <div style={{ padding: 24, background: '#fff' }}>
            <Form layout="vertical">
                {(schema.fields || []).map((field) => (
                    <FieldRenderer
                        key={field.id}
                        field={field}
                        sqliteFiles={sqliteFiles}
                        sqlJs={sqlJs}
                    />
                ))}
            </Form>
        </div>
    );
};
